import { useSdk } from '~/sdk';
import type { ValidateVatData } from '@vue-storefront/prestashop-api';

export function useVatValidation() {
  const result = ref<ValidateVatData | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let validationId = 0;

  async function validate(vatNumber: string, countryCode?: string) {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    const currentValidationId = ++validationId;
    if (!vatNumber?.trim()) {
      result.value = null;
      error.value = null;
      loading.value = false;
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await useSdk().commerce.validateVat({
        vat_number: vatNumber.trim(),
        ...(countryCode?.trim() ? { country_code: countryCode.trim() } : {}),
      });
      if (currentValidationId !== validationId) return;
      if (!response?.success) {
        result.value = null;
        error.value = response?.psdata?.message || response?.message || 'VAT number is not valid';
        return;
      }

      const data = response.psdata;
      result.value = data
        ? { ...data, valid: ['true', '1'].includes(String(data.valid).toLowerCase()) }
        : null;
    } catch {
      if (currentValidationId !== validationId) return;
      error.value = 'VAT validation unavailable';
      result.value = null;
    } finally {
      if (currentValidationId === validationId) loading.value = false;
    }
  }

  function validateDebounced(vatNumber: string, countryCode?: string, delayMs = 500) {
    validationId++;
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
    result.value = null;
    error.value = null;
    loading.value = Boolean(vatNumber?.trim());
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      validate(vatNumber, countryCode);
    }, delayMs);
  }

  function reset() {
    validationId++;
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    result.value = null;
    error.value = null;
    loading.value = false;
  }

  return { result, loading, error, validate, validateDebounced, reset };
}

